import { useEffect, useMemo, useState } from "react";
import { useDataProvider, useGetMany, useNotify, useRecordContext } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";

import { callApi } from "@/dataprovider/misc-apis";
import { exportMiqaatAttendanceToExcel } from "../miqaat-attendance/export-miqaat-attendance";
import MiqaatAttendanceAuditCard, { type AttendanceTurnoutData } from "./MiqaatAttendanceAuditCard";

type DemographicsResponse = {
  totalRsvpAttendees: number;
  male: number;
  female: number;
  childrenUnder10: number;
  scanned?: AttendanceTurnoutData;
};

type Props = {
  embedded?: boolean;
};

export default function MiqaatAttendanceAudit({ embedded = false }: Props) {
  const record = useRecordContext();
  const venueIds = useMemo(() => {
    const ids = record?.venueIds;
    return Array.isArray(ids) ? ids.filter((id): id is string => Boolean(id)) : [];
  }, [record?.venueIds]);

  const { data: venues } = useGetMany(
    "miqaatVenues",
    { ids: venueIds },
    { enabled: venueIds.length > 0 }
  );

  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [venueId, setVenueId] = useState("");
  const [data, setData] = useState<DemographicsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!record?.id) return;
    setExporting(true);
    try {
      await exportMiqaatAttendanceToExcel(dataProvider, {
        miqaatId: String(record.id),
        miqaatName: String(record.name || "miqaat"),
        venueId: venueId || undefined,
      });
      notify("Attendance records exported successfully", { type: "info" });
    } catch (err) {
      console.error("Failed to export attendance records", err);
      notify(err instanceof Error ? err.message : "Failed to export attendance records", {
        type: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (venueId && venueIds.length > 0 && !venueIds.includes(venueId)) {
      setVenueId("");
    }
  }, [venueId, venueIds]);

  useEffect(() => {
    const id = record?.id;
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void callApi({
      location: "miqaats",
      method: "GET",
      id: `${String(id)}/rsvp-demographics`,
      data: venueId ? { venueId } : {},
    })
      .then((res) => {
        if (!cancelled) {
          setData((res.data ?? null) as DemographicsResponse | null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load turnout audit");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [record?.id, venueId]);

  if (!record?.id) {
    return null;
  }

  const venueChoices = venueIds.map((id) => {
    const venue = (venues ?? []).find((v) => String(v.id) === id);
    return { id, name: venue?.name ? String(venue.name) : id };
  });

  const totalScanned = data?.scanned?.totalScannedAttendees ?? 0;

  const content = (
    <>
      {venueChoices.length > 0 && (
        <Box sx={{ mb: 2, maxWidth: 360 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="miqaat-attendance-audit-venue-label">Filter by venue</InputLabel>
            <Select
              labelId="miqaat-attendance-audit-venue-label"
              label="Filter by venue"
              value={venueId}
              onChange={(e) => setVenueId(String(e.target.value))}
            >
              <MenuItem value="">
                <em>All venues</em>
              </MenuItem>
              {venueChoices.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading && <CircularProgress size={24} sx={{ my: 2 }} />}

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {!loading && !error && data && totalScanned > 0 && data.scanned && (
        <MiqaatAttendanceAuditCard
          scanned={data.scanned}
          totalRsvpAttendees={data.totalRsvpAttendees}
          onExport={handleExport}
          isExporting={exporting}
        />
      )}

      {!loading && !error && data && totalScanned === 0 && (
        <Card
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, textAlign: "center", bgcolor: "background.paper" }}
        >
          <Stack spacing={1} alignItems="center">
            <FactCheckOutlinedIcon sx={{ fontSize: 36, color: "text.secondary" }} />
            <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
              No Attendance Scans Yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 460 }}>
              Attendance records for this occurrence will populate here automatically once members
              are checked in via admin scanners or WhatsApp self-scan.
            </Typography>
          </Stack>
        </Card>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ width: "100%" }}>{content}</Box>;
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, width: "100%" }}>
      <CardContent sx={{ "&:last-child": { pb: 2.5 } }}>{content}</CardContent>
    </Card>
  );
}
